import React, { useState, useEffect, useMemo } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import api from '../api/axiosConfig';
import { useToast } from '../hooks/useToast';
import AddressAutocomplete from './ui/AddressAutocomplete';
import { AddressDisplay } from '../utils/addressFormatter';

const TransferPlani1 = () => {
  const { showSuccess, showError, showWarning } = useToast();
  const [transfers, setTransfers] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [driversLoading, setDriversLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTransfer, setEditingTransfer] = useState(null);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [previousTransferCount, setPreviousTransferCount] = useState(0);
  const [notificationEnabled, setNotificationEnabled] = useState(true);
  // Local timezone için doğru tarih formatı
  const [selectedDate, setSelectedDate] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  });
  const [contextMenu, setContextMenu] = useState({ show: false, x: 0, y: 0, transfer: null });

  // Browser notification izni iste (ilk yüklemede)
  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, []);

  useEffect(() => {
    loadTransfers();
    loadDrivers();
  }, [selectedDate]); // selectedDate değiştiğinde yeniden yükle
  
  // Transfer sayısı değiştiğinde bildirim kontrolü
  useEffect(() => {
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    
    // Sadece bugün için bildirim
    if (selectedDate === today && previousTransferCount > 0 && transfers.length > previousTransferCount) {
      const diff = transfers.length - previousTransferCount;
      playNotification(`🔔 ${diff} yeni transfer talebi eklendi!`);
    }
    
    // Transfer sayısını güncelle
    if (transfers.length > 0) {
      setPreviousTransferCount(transfers.length);
    }
  }, [transfers.length, selectedDate]); // Transfer sayısı değiştiğinde kontrol et
  
  // Auto-refresh sadece bugün için (performans optimizasyonu)
  useEffect(() => {
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    
    // Sadece bugüne bakıyorsak otomatik yenile
    if (selectedDate === today) {
      const interval = setInterval(() => {
        loadTransfers();
        loadDrivers();
      }, 30000);
      
      return () => clearInterval(interval);
    }
  }, [selectedDate]);

  // Context menu'yu kapatmak için click ve ESC listener
  useEffect(() => {
    const handleClickOutside = () => {
      setContextMenu({ show: false, x: 0, y: 0, transfer: null });
    };

    const handleEscKey = (e) => {
      if (e.key === 'Escape') {
        setContextMenu({ show: false, x: 0, y: 0, transfer: null });
      }
    };

    if (contextMenu.show) {
      document.addEventListener('click', handleClickOutside);
      document.addEventListener('keydown', handleEscKey);
      return () => {
        document.removeEventListener('click', handleClickOutside);
        document.removeEventListener('keydown', handleEscKey);
      };
    }
  }, [contextMenu.show]);

  // Bildirim fonksiyonu (ses + toast + browser notification)
  const playNotification = (message) => {
    if (!notificationEnabled) return;
    
    try {
      // 1. Çift Beep Sesi (WhatsApp tarzı)
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // İlk beep
      const oscillator1 = audioContext.createOscillator();
      const gainNode1 = audioContext.createGain();
      
      oscillator1.connect(gainNode1);
      gainNode1.connect(audioContext.destination);
      
      oscillator1.frequency.value = 800; // 800 Hz
      oscillator1.type = 'sine';
      
      gainNode1.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode1.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.15);
      
      oscillator1.start(audioContext.currentTime);
      oscillator1.stop(audioContext.currentTime + 0.15); // Kısa beep (0.15s)
      
      // İkinci beep (0.2 saniye sonra)
      const oscillator2 = audioContext.createOscillator();
      const gainNode2 = audioContext.createGain();
      
      oscillator2.connect(gainNode2);
      gainNode2.connect(audioContext.destination);
      
      oscillator2.frequency.value = 800; // Aynı ton
      oscillator2.type = 'sine';
      
      gainNode2.gain.setValueAtTime(0.3, audioContext.currentTime + 0.2);
      gainNode2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.35);
      
      oscillator2.start(audioContext.currentTime + 0.2); // 0.2s sonra başla
      oscillator2.stop(audioContext.currentTime + 0.35); // 0.15s süre
      
      // 2. Toast göster
      showSuccess(message);
      
      // 3. Browser notification (izin varsa)
      if ("Notification" in window && Notification.permission === "granted") {
        new Notification("BHT Transfer Sistemi", {
          body: message,
          icon: "/favicon.ico",
          badge: "/favicon.ico"
        });
      }
    } catch (error) {
      console.error('Bildirim hatası:', error);
      // Hata olsa bile toast göster
      showSuccess(message);
    }
  };

  const loadTransfers = async () => {
    try {
      setLoading(true);
      
      console.log('🔍 SEÇİLİ TARİH:', selectedDate);
      
      // SADECE seçili tarihin transferlerini getir (from_date yerine date)
      const response = await api.get('/api/admin/transfers', {
        params: {
          date: selectedDate,  // Tam eşleşme (sadece bu tarih)
          size: 100  // Maksimum 100 transfer göster
        },
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      const transferData = response.data.transfers || response.data || [];
      
      console.log('🔍 API Parametresi:', { date: selectedDate, size: 100 });
      console.log('🔍 Gelen Transfer Sayısı:', transferData.length);
      
      setTransfers(transferData);
    } catch (error) {
      console.error('Transferler yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDrivers = async () => {
    try {
      setDriversLoading(true);
      
      // Şoförleri atanan transferlerle birlikte al (seçili tarihe göre)
      const response = await api.get('/api/admin/drivers', {
        params: {
          include_transfers: true,
          target_date: selectedDate
        }
      });
      const driversData = response.data.drivers || response.data || [];
      
      // Seçili tarihin bir gün öncesi (C vardiyası için)
      const selectedDateObj = new Date(selectedDate);
      const previousDate = new Date(selectedDateObj);
      previousDate.setDate(selectedDateObj.getDate() - 1);
      const previousDateStr = `${previousDate.getFullYear()}-${String(previousDate.getMonth() + 1).padStart(2, '0')}-${String(previousDate.getDate()).padStart(2, '0')}`;
      
      // Her şoför için vardiya bilgilerini al
      const driversWithShifts = await Promise.all(
        driversData.map(async (driver) => {
          try {
            // 1. Seçili tarih vardiyası (A ve B vardiyası için)
            const todayShiftResponse = await api.get(`/api/shifts/for-date`, {
              params: {
                driver_id: driver.id,
                shift_date: selectedDate
              }
            });
            
            // 2. Bir gün önceki vardiya (C vardiyası için)
            const yesterdayShiftResponse = await api.get(`/api/shifts/for-date`, {
              params: {
                driver_id: driver.id,
                shift_date: previousDateStr
              }
            });
            
            // 3. Vardiya seçim mantığı - YENİ MANTIK
            let todayShift = null;      // A ve B vardiyaları için
            let yesterdayShift = null;  // C vardiyası için
            
            // Bugünkü A veya B vardiyası
            if (todayShiftResponse.data) {
              todayShift = todayShiftResponse.data;
            }
            
            // Dünkü C vardiyası
            if (yesterdayShiftResponse.data?.shift_type === 'C_SHIFT') {
              yesterdayShift = yesterdayShiftResponse.data;
            }
            
            return {
              ...driver,
              todayShift: todayShift,           // A ve B için
              yesterdayShift: yesterdayShift,   // C vardiyası için
              assignedTransfers: driver.assigned_transfers || []
            };
          } catch (error) {
            console.error(`Şoför ${driver.id} vardiya bilgileri alınamadı:`, error);
            return {
              ...driver,
              todayShift: null,
              yesterdayShift: null,
              assignedTransfers: driver.assigned_transfers || []
            };
          }
        })
      );
      
      setDrivers(driversWithShifts);
    } catch (error) {
      console.error('Şoförler yüklenemedi:', error);
      setDrivers([]);
    } finally {
      setDriversLoading(false);
    }
  };

  const formatDateTime = (date, time) => {
    if (!date || !time) return '-';
    const dateObj = new Date(`${date}T${time}`);
    return dateObj.toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getServiceTypeLabel = (type) => {
    const types = {
      'PATIENT': 'Hasta',
      'STAFF': 'Personel', 
      'EXECUTIVE': 'Yönetici',
      'DOCUMENT': 'Evrak',
      'AIRPORT': 'Havalimanı',
      'MATERIAL': 'Malzeme'
    };
    return types[type] || type;
  };

  const getStatusLabel = (status) => {
    const statuses = {
      'RECEIVED': 'Talep Alındı',
      'PLANNED': 'Planlandı',
      'ENROUTE': 'Yola Çıktı',
      'AT_PICKUP': 'Konumda',
      'PICKED_UP': 'Yolcu Alındı', 
      'COMPLETED': 'Tamamlandı'
    };
    return statuses[status] || status;
  };

  // Şoförleri vardiya tipine göre gruplandır ve sırala
  const groupAndSortDriversByShift = (drivers) => {
    // Şoförleri vardiya tipine göre gruplandır
    const groups = {
      A_SHIFT: [],
      B_SHIFT: [],
      C_SHIFT: [],
      OFF_DUTY: [] // İzinli
    };

    drivers.forEach(driver => {
      // A ve B vardiyası için bugünkü vardiyaya bak
      if (driver.todayShift) {
        const shiftType = driver.todayShift.shift_type;
        if (shiftType === 'A_SHIFT' || shiftType === 'B_SHIFT') {
          groups[shiftType].push(driver);
          return;
        }
      }
      
      // C vardiyası için dünkü vardiyaya bak
      if (driver.yesterdayShift && driver.yesterdayShift.shift_type === 'C_SHIFT') {
        groups.C_SHIFT.push(driver);
        return;
      }
      
      // Hiçbir vardiyası yoksa izinli
      groups.OFF_DUTY.push(driver);
    });

    // Her grubu isim sırasına göre sırala
    Object.keys(groups).forEach(key => {
      groups[key].sort((a, b) => {
        const nameA = (a.user_name || '').toLowerCase();
        const nameB = (b.user_name || '').toLowerCase();
        return nameA.localeCompare(nameB, 'tr');
      });
    });

    return groups;
  };

  // Vardiya tipinin görünen adını döndür
  const getShiftGroupName = (shiftType) => {
    const names = {
      A_SHIFT: 'Sabah Vardiyası (08:00 - 17:30)',
      B_SHIFT: 'Akşam Vardiyası (15:00 - 00:30)',
      C_SHIFT: 'Gece Vardiyası (23:00 - 08:00)',
      OFF_DUTY: 'İzinli Şoförler'
    };
    return names[shiftType] || shiftType;
  };

  // Transfer kartlarını sırala: Önce tarihe göre (artan), sonra RECEIVED olanlar öne
  const sortedTransfers = useMemo(() => {
    return [...transfers].sort((a, b) => {
      // 1. Önce tarihe göre sırala (artan - en yakın tarih solda)
      const dateA = new Date(`${a.pickup_date}T${a.pickup_time}`);
      const dateB = new Date(`${b.pickup_date}T${b.pickup_time}`);
      const dateCompare = dateA - dateB;
      
      if (dateCompare !== 0) return dateCompare;
      
      // 2. Aynı tarihte: RECEIVED durumu öne gelsin
      const statusPriority = { 'RECEIVED': 1, 'PLANNED': 2 };
      return (statusPriority[a.status] || 3) - (statusPriority[b.status] || 3);
    });
  }, [transfers]);

  // Tarih değiştirme fonksiyonu
  const changeDate = (days) => {
    const current = new Date(selectedDate);
    current.setDate(current.getDate() + days);
    const newDate = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}-${String(current.getDate()).padStart(2, '0')}`;
    setSelectedDate(newDate);
  };

  const handleOperationPlan = async () => {
    if (transfers.length === 0) {
      showWarning('Planlanacak transfer talebi bulunmuyor!');
      return;
    }

    setIsOptimizing(true);
    
    try {
      console.log('🚀 Greedy optimizasyon başlatılıyor...', selectedDate);
      
      // Greedy algoritması API'sini çağır - seçili tarihi planla
      const response = await api.post('/api/optimization/greedy', {
        target_date: selectedDate
      }, { timeout: 120000 });
      
      console.log('✅ Optimizasyon sonucu:', response.data);
      
      // Başarı mesajı
      const summary = response.data.summary || {};
      const totalTransfers = summary.total_transfers || 0;
      const totalAssignments = summary.total_assignments || 0;
      
      let message = `Operasyon planı başarıyla oluşturuldu!\n\n`;
      message += `📊 Toplam Transfer: ${totalTransfers}\n`;
      message += `✅ Atanan Transfer: ${totalAssignments}\n`;
      
      if (summary.shifts) {
        message += `\n📋 Vardiya Dağılımı:\n`;
        if (summary.shifts.A_SHIFT) {
          message += `• A Vardiya: ${summary.shifts.A_SHIFT.assignments} atama\n`;
        }
        if (summary.shifts.B_SHIFT) {
          message += `• B Vardiya: ${summary.shifts.B_SHIFT.assignments} atama\n`;
        }
        if (summary.shifts.C_SHIFT) {
          message += `• C Vardiya: ${summary.shifts.C_SHIFT.assignments} atama\n`;
        }
      }
      
      showSuccess(message);
      
      // Tüm verileri yenile
      await Promise.all([
        loadTransfers(),
        loadDrivers()
      ]);
      
      
    } catch (error) {
      console.error('❌ Operasyon planı hatası:', error);
      
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.message || 
                          error.message || 
                          'Bilinmeyen hata';
      
      showError(`Operasyon planı oluşturulamadı! Hata: ${errorMessage} Lütfen tekrar deneyin.`);
    } finally {
      setIsOptimizing(false);
    }
  };

  // Drag & Drop Handlers
  const onDragStart = (start) => {
    console.log('🎯 Drag başladı:', start);
  };

  const onDragEnd = async (result) => {
    console.log('🔚 Drag bitti:', result);
    const { destination, source, draggableId, type } = result;
    
    // Sadece TRANSFER type'ında işlem yap
    if (type !== 'TRANSFER') {
      return;
    }
    
    // Drag iptal edildi
    if (!destination) {
      console.log('❌ Drag iptal edildi - destination yok');
      return;
    }
    
    // Aynı yere bırakıldı
    if (destination.droppableId === source.droppableId) {
      console.log('ℹ️ Aynı yere bırakıldı');
      return;
    }
    
    const transferId = parseInt(draggableId.replace('transfer-', ''));
    // droppableId formatı: "driver-{id}-{shiftType}" 
    const driverIdMatch = destination.droppableId.match(/driver-(\d+)-/);
    const newDriverId = driverIdMatch ? parseInt(driverIdMatch[1]) : null;
    
    if (!newDriverId) {
      console.error('❌ Driver ID parse edilemedi:', destination.droppableId);
      return;
    }
    
    console.log('🔄 Transfer değiştiriliyor:', {
      transferId,
      newDriverId,
      source: source.droppableId,
      destination: destination.droppableId
    });
    
    try {
      // API ile transfer değiştir
      const response = await api.post('/api/v1/admin/transfers/reassign', {
        transfer_id: transferId,
        new_driver_id: newDriverId,
        reason: 'Admin drag&drop ile değiştirdi'
      });
      
      if (response.data.success) {
        showSuccess(`Transfer başarıyla yeni şoföre atandı!`);
        
        // Verileri yenile
        await Promise.all([
          loadTransfers(),
          loadDrivers()
        ]);
        
        console.log('✅ Transfer başarıyla değiştirildi');
      } else {
        showError('Transfer değiştirilemedi: ' + response.data.message);
      }
      
    } catch (error) {
      console.error('❌ Transfer değiştirme hatası:', error);
      showError('Transfer değiştirilemedi: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Manuel transfer atama
  const handleManualAssignment = async (transferId, driverId) => {
    try {
      const response = await api.post('/api/v1/admin/transfers/reassign', {
        transfer_id: transferId,
        new_driver_id: driverId,
        reason: 'Admin manuel ataması'
      });
      
      if (response.data.success) {
        showSuccess(`Transfer başarıyla şoföre atandı!`);
        
        // Verileri yenile
        await Promise.all([
          loadTransfers(),
          loadDrivers()
        ]);
        
        console.log('✅ Manuel atama başarılı');
      } else {
        showError('Transfer atanamadı: ' + response.data.message);
      }
      
    } catch (error) {
      console.error('❌ Manuel atama hatası:', error);
      showError('Transfer atanamadı: ' + (error.response?.data?.detail || error.message));
    } finally {
      setContextMenu({ show: false, x: 0, y: 0, transfer: null });
    }
  };

  // Transfer silme
  const handleDeleteTransfer = async (transferId) => {
    try {
      const response = await api.delete(`/api/transfers/${transferId}`);
      
      if (response.status === 200 || response.data.success) {
        showSuccess(`Transfer #${transferId} başarıyla silindi!`);
        
        // Verileri yenile
        await loadTransfers();
        
        console.log('✅ Transfer silindi:', transferId);
      }
      
    } catch (error) {
      console.error('❌ Transfer silme hatası:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Transfer silinemedi';
      showError(`Transfer silinemedi: ${errorMessage}`);
    } finally {
      setContextMenu({ show: false, x: 0, y: 0, transfer: null });
    }
  };

  // Sağ tık context menu
  const handleRightClick = (e, transfer) => {
    e.preventDefault();
    
    // Tüm transferler için context menu (artık sadece RECEIVED değil)
    setContextMenu({
      show: true,
      x: e.clientX,
      y: e.clientY,
      transfer: transfer
    });
  };

  return (
    <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
      <div className="min-h-screen bg-gray-50 relative">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
          
          {/* Transfer Talepleri Paneli */}
          <div className="bg-white p-6 rounded-lg shadow-sm mb-8">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Transfer Talepleri</h2>
                <p className="text-gray-600">Bekleyen ve planlanan transfer talepleri ({transfers.length})</p>
              </div>
              
              <div className="flex items-center space-x-3">
                {/* Tarih Değiştirme Butonları */}
                <div className="flex items-center space-x-2 bg-gray-100 rounded-lg p-2">
                  <button
                    onClick={() => changeDate(-1)}
                    className="p-2 text-gray-600 hover:text-gray-900 hover:bg-white rounded transition-colors"
                    title="Önceki gün"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <div className="flex flex-col items-center px-3">
                    <span className="text-sm font-semibold text-gray-900">
                      {new Date(selectedDate).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })}
                    </span>
                    <span className="text-xs text-gray-500">
                      {new Date(selectedDate).toLocaleDateString('tr-TR', { weekday: 'short' })}
                    </span>
                  </div>
                  <button
                    onClick={() => changeDate(1)}
                    className="p-2 text-gray-600 hover:text-gray-900 hover:bg-white rounded transition-colors"
                    title="Sonraki gün"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
                
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="px-4 py-2 text-sm font-medium rounded-md bg-red-500 text-white hover:bg-red-600 flex items-center space-x-2 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  <span>Yeni Transfer</span>
                </button>
                
                {/* Operasyonu Planla - Basit Buton */}
                <button
                  onClick={handleOperationPlan}
                  disabled={isOptimizing || loading}
                  className={`px-4 py-2 text-sm font-medium rounded-md flex items-center space-x-2 transition-colors ${
                    isOptimizing || loading
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-green-500 text-white hover:bg-green-600'
                  }`}
                >
                  {isOptimizing ? (
                    <div className="flex items-center space-x-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Planlanıyor...</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      <span>Operasyon Planla</span>
                    </div>
                  )}
                </button>
              </div>
            </div>

            {/* Transfer Kartları */}
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500 mx-auto"></div>
                <p className="mt-2 text-gray-600">Yükleniyor...</p>
              </div>
            ) : transfers.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <svg className="w-12 h-12 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <p className="font-medium">Henüz transfer talebi bulunmuyor</p>
              </div>
            ) : (
              <div className="overflow-x-auto pb-4">
                <div className="flex space-x-4 min-w-max">
                  {sortedTransfers.map(transfer => (
                    <div 
                      key={transfer.id} 
                      className="w-72 bg-white rounded-lg shadow-sm border border-gray-200 flex-shrink-0 transition-all hover:shadow-md cursor-context-menu"
                      onContextMenu={(e) => handleRightClick(e, transfer)}
                      title="Sağ tıklayarak işlem yapabilirsiniz"
                    >
                      {/* Header */}
                      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
                        <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded">
                          {transfer.agency_name || 'BHT CLINIC'}
                        </span>
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500 text-xs">#{transfer.id}</span>
                          {transfer.status === 'RECEIVED' && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingTransfer(transfer);
                                setShowEditModal(true);
                              }}
                              className="p-0.5 hover:bg-gray-100 rounded"
                              title="Transfer'i düzenle"
                            >
                              <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="p-3 space-y-2.5">
                        {/* Nereden */}
                        <div>
                          <div className="text-xs text-gray-500 mb-0.5">Nereden</div>
                          <AddressDisplay address={transfer.pickup_addr} className="font-bold text-gray-900 text-sm" />
                        </div>

                        {/* Nereye */}
                        <div>
                          <div className="text-xs text-gray-500 mb-0.5">Nereye</div>
                          <AddressDisplay address={transfer.dropoff_addr} className="font-bold text-gray-900 text-sm" />
                        </div>

                        {/* Tarih/Saat */}
                        <div>
                          <div className="text-xs text-gray-500 mb-0.5">Tarih/Saat</div>
                          <div className="font-bold text-gray-900 text-sm">
                            {formatDateTime(transfer.pickup_date, transfer.pickup_time)}
                          </div>
                        </div>

                        {/* Yolcu Sayısı & Hizmet Tipi */}
                        <div className="flex gap-3">
                          <div className="flex-1">
                            <div className="text-xs text-gray-500 mb-0.5">Yolcu Sayısı</div>
                            <div className="font-bold text-gray-900 text-sm">{transfer.pax_count}</div>
                          </div>
                          <div className="flex-1">
                            <div className="text-xs text-gray-500 mb-0.5">Hizmet Tipi</div>
                            <div className="font-bold text-gray-900 text-sm">{getServiceTypeLabel(transfer.service_type) || 'Standart'}</div>
                          </div>
                        </div>

                        {/* Yolcu Bilgi */}
                        {transfer.passenger_name && (
                          <div>
                            <div className="text-xs text-gray-500 mb-0.5">Yolcu Bilgi</div>
                            <div className="font-bold text-gray-900 text-xs leading-tight break-words">
                              {transfer.passenger_name}
                              {transfer.passenger_id_no && ` / ${transfer.passenger_id_no}`}
                            </div>
                          </div>
                        )}

                        {/* Uçuş Kodu */}
                        {transfer.flight_code && (
                          <div>
                            <div className="text-xs text-gray-500 mb-0.5">Uçuş Kodu</div>
                            <div className="font-bold text-gray-900 text-sm">{transfer.flight_code}</div>
                          </div>
                        )}

                        {/* Sorumlu Kişi */}
                        {transfer.responsible_person && (
                          <div>
                            <div className="text-xs text-gray-500 mb-0.5">Sorumlu Kişi</div>
                            <div className="font-bold text-gray-900 text-sm">{transfer.responsible_person}</div>
                            {transfer.responsible_contact && (
                              <div className="text-xs text-gray-600 mt-0.5">
                                {transfer.responsible_contact}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Durum & Şoför */}
                        <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                          <div className={`px-2 py-0.5 text-xs font-semibold rounded ${
                            transfer.status === 'RECEIVED' ? 'text-orange-700 bg-orange-100' :
                            transfer.status === 'PLANNED' ? 'text-green-700 bg-green-100' :
                            'text-gray-700 bg-gray-100'
                          }`}>
                            {getStatusLabel(transfer.status)}
                          </div>
                          {transfer.driver_name && (
                            <div className="text-xs text-gray-900 font-medium">
                              {transfer.driver_name}
                            </div>
                          )}
                        </div>

                        {/* Not */}
                        {transfer.notes && (
                          <div className="pt-2 border-t border-gray-100">
                            <div className="text-xs text-gray-500 mb-0.5">Not:</div>
                            <div className="text-xs text-gray-700 line-clamp-2">{transfer.notes}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Operasyon Planı Paneli - Drag & Drop Enabled */}
          <div className="bg-gray-50 p-6 md:p-8">
            <header className="flex justify-between items-center mb-6">
              <div className="flex items-center space-x-4">
                <h1 className="text-3xl font-bold text-gray-900">
                  {new Date(selectedDate).toLocaleDateString('tr-TR', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })} Operasyon Planı
                </h1>
              </div>
              
              {/* Tarih Değiştirme Butonları */}
              <div className="flex items-center space-x-2 bg-white rounded-lg p-2 shadow-sm">
                <button
                  onClick={() => changeDate(-1)}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
                  title="Önceki günün operasyon planı"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <div className="flex flex-col items-center px-3">
                  <span className="text-sm font-semibold text-gray-900">
                    {new Date(selectedDate).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })}
                  </span>
                  <span className="text-xs text-gray-500">
                    {new Date(selectedDate).toLocaleDateString('tr-TR', { weekday: 'short' })}
                  </span>
                </div>
                <button
                  onClick={() => changeDate(1)}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
                  title="Sonraki günün operasyon planı"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </header>

            {/* Şoför Kartları */}
            {driversLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500 mx-auto"></div>
                <p className="mt-2 text-gray-600">Şoförler yükleniyor...</p>
              </div>
            ) : drivers.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <svg className="w-12 h-12 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <p className="font-medium">Henüz şoför bulunmuyor</p>
              </div>
            ) : (
              <div className="space-y-8">
                {Object.entries(groupAndSortDriversByShift(drivers)).map(([shiftType, shiftDrivers]) => {
                  if (shiftDrivers.length === 0) return null;
                  
                  return (
                    <div key={shiftType} className="space-y-4">
                      {/* Vardiya Grup Başlığı */}
                      <div className="flex items-center space-x-4">
                        <h3 className="text-lg font-semibold text-gray-800">
                          {getShiftGroupName(shiftType)}
                        </h3>
                        <div className="flex-1 h-px bg-gray-300"></div>
                        <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                          {shiftDrivers.length} şoför
                        </span>
                      </div>
                      
                      {/* Şoför Kartları */}
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {shiftDrivers.map((driver, driverIndex) => {
                          const droppableId = `driver-${driver.id}-${shiftType}`;
                          return (
                            <Droppable 
                              droppableId={droppableId} 
                              key={`${shiftType}-driver-${driver.id}`}
                              type="TRANSFER"
                            >
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.droppableProps}
                                  className={`relative ${
                                    snapshot.isDraggingOver 
                                      ? 'ring-4 ring-blue-400 ring-offset-2 bg-blue-50 rounded-lg' 
                                      : ''
                                  }`}
                                  style={{
                                    transition: 'all 0.2s ease',
                                    transform: snapshot.isDraggingOver ? 'scale(1.02)' : 'scale(1)'
                                  }}
                                >
                                  <DriverCard 
                                    driver={driver}
                                    isDraggingOver={snapshot.isDraggingOver}
                                  />
                                  {provided.placeholder}
                                  
                                  {/* Drop Zone Overlay */}
                                  {snapshot.isDraggingOver && (
                                    <div className="absolute inset-0 bg-blue-400 bg-opacity-10 rounded-lg pointer-events-none flex items-center justify-center">
                                      <div className="bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-semibold shadow-lg">
                                        🎯 Buraya Bırak
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </Droppable>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Context Menu - İşlemler */}
          {contextMenu.show && contextMenu.transfer && (
            <div 
              className="fixed bg-white rounded-lg shadow-2xl border border-gray-200 py-2 min-w-[200px] z-50"
              style={{
                left: contextMenu.x,
                top: contextMenu.y,
                transform: 'translate(-50%, 0)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-3 py-2 border-b border-gray-100">
                <p className="text-sm font-semibold text-gray-900">
                  {contextMenu.transfer.status === 'RECEIVED' ? 'Şoför Atama' : 'İşlemler'}
                </p>
                <p className="text-xs text-gray-600">
                  Transfer #{contextMenu.transfer.id} - {contextMenu.transfer.pickup_time}
                </p>
              </div>
              
              {/* Şoför listesi sadece RECEIVED durumunda göster */}
              {contextMenu.transfer.status === 'RECEIVED' && (
                <div className="max-h-60 overflow-y-auto">
                  {drivers
                    .filter(driver => driver.todayShift !== null || driver.yesterdayShift !== null) // Sadece çalışan şoförler (A, B veya C vardiyası)
                    .map(driver => {
                      const shiftInfo = driver.todayShift || driver.yesterdayShift;
                      return (
                        <button
                          key={driver.id}
                          onClick={() => handleManualAssignment(contextMenu.transfer.id, driver.id)}
                          className="w-full px-3 py-2 text-left hover:bg-gray-100 flex items-center space-x-2 transition-colors"
                        >
                          <svg className="w-4 h-4 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                          </svg>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">{driver.user_name}</p>
                            <p className="text-xs text-gray-600">
                              {shiftInfo ? getShiftGroupName(shiftInfo.shift_type) : 'Vardiya yok'}
                            </p>
                          </div>
                          <div className="text-xs text-gray-500">
                            {driver.assignedTransfers?.length || 0} transfer
                          </div>
                        </button>
                      );
                    })}
                  
                  {drivers.filter(driver => driver.todayShift !== null || driver.yesterdayShift !== null).length === 0 && (
                    <div className="px-3 py-4 text-center text-gray-500">
                      <svg className="w-8 h-8 mx-auto mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <p className="text-sm">Bugün çalışan şoför yok</p>
                    </div>
                  )}
                </div>
              )}
              
              {/* Sil butonu her zaman göster */}
              <div className={contextMenu.transfer.status === 'RECEIVED' ? 'border-t border-gray-100' : ''}>
                <button
                  onClick={() => handleDeleteTransfer(contextMenu.transfer.id)}
                  className="w-full px-3 py-2 text-left hover:bg-red-50 flex items-center space-x-2 transition-colors text-red-600"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  <span className="text-sm font-medium">Sil</span>
                </button>
              </div>
              
              <div className="px-3 py-2 border-t border-gray-100">
                <button
                  onClick={() => setContextMenu({ show: false, x: 0, y: 0, transfer: null })}
                  className="w-full text-center text-xs text-gray-500 hover:text-gray-700"
                >
                  Kapat (ESC)
                </button>
              </div>
            </div>
          )}

          {/* Transfer Oluşturma Modal'ı */}
          {showCreateModal && (
            <TransferCreateModal 
              onClose={() => setShowCreateModal(false)}
              onSuccess={() => {
                setShowCreateModal(false);
                loadTransfers();
              }}
            />
          )}

          {/* Transfer Düzenleme Modal'ı */}
          {showEditModal && editingTransfer && (
            <TransferEditModal 
              transfer={editingTransfer}
              onClose={() => {
                setShowEditModal(false);
                setEditingTransfer(null);
              }}
              onSuccess={() => {
                setShowEditModal(false);
                setEditingTransfer(null);
                loadTransfers();
              }}
            />
          )}


        </div>
      </div>
    </DragDropContext>
  );
};

// Şoför Kartı Bileşeni - Drag & Drop Enabled
const DriverCard = ({ driver, isDraggingOver }) => {
  // distanceData state kaldırıldı - Google Maps API maliyetini önlemek için

  const getShiftTypeLabel = (shiftType) => {
    const types = {
      'A_SHIFT': 'A Vardiya',
      'B_SHIFT': 'B Vardiya', 
      'C_SHIFT': 'C Vardiya',
      'FULL_DAY': 'Tam Gün'
    };
    return types[shiftType] || shiftType;
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    // Eğer time string formatındaysa (HH:MM:SS), sadece HH:MM kısmını al
    return timeStr.toString().substring(0, 5);
  };

  // Şoförün çalışıp çalışmadığını kontrol et (A, B veya C vardiyası)
  const isWorking = driver.todayShift !== null || driver.yesterdayShift !== null;
  
  // Gösterilecek vardiya bilgisi (A/B için bugün, C için dün)
  const displayShift = driver.todayShift || driver.yesterdayShift;

  const getAgencyBadgeColor = (agencyName) => {
    if (agencyName === 'BHT Clinic Admin') return 'bg-blue-200 text-blue-800';
    return 'bg-yellow-200 text-yellow-800';
  };

  const calculateProgress = (transfer) => {
    if (!transfer) return { 
      progress: 0, 
      label: 'Beklemede', 
      color: 'bg-gray-400',
      badgeClass: 'bg-gray-100 text-gray-800'
    };
    
    const status = transfer.status || 'RECEIVED';
    
    switch (status) {
      case 'RECEIVED':
        return { 
          progress: 10, 
          label: 'Talep Alındı', 
          color: 'bg-yellow-500',
          badgeClass: 'bg-yellow-100 text-yellow-800'
        };
      case 'PLANNED':
        return { 
          progress: 25, 
          label: 'Planlandı', 
          color: 'bg-blue-500',
          badgeClass: 'bg-blue-100 text-blue-800'
        };
      case 'ENROUTE':
        return { 
          progress: 40, 
          label: 'Şoför Yolda', 
          color: 'bg-orange-500',
          badgeClass: 'bg-orange-100 text-orange-800'
        };
      case 'AT_PICKUP':
        return { 
          progress: 60, 
          label: 'Şoför Konumda', 
          color: 'bg-indigo-500',
          badgeClass: 'bg-indigo-100 text-indigo-800'
        };
      case 'PICKED_UP':
        return { 
          progress: 80, 
          label: 'Yolcu Araçta', 
          color: 'bg-purple-500',
          badgeClass: 'bg-purple-100 text-purple-800'
        };
      case 'COMPLETED':
        return { 
          progress: 100, 
          label: 'Transfer Tamamlandı', 
          color: 'bg-green-500',
          badgeClass: 'bg-green-100 text-green-800'
        };
      default:
        return { 
          progress: 0, 
          label: 'Bilinmeyen', 
          color: 'bg-gray-400',
          badgeClass: 'bg-gray-100 text-gray-800'
        };
    }
  };

  // Mesafe/süre bilgisini getir (backend'den gelen veri)
  const getDistanceData = (transfer) => {
    // Debug: Veri kontrolü - Artık çalışıyor, log kaldırıldı
    // console.log(`🔍 Transfer ${transfer.id}:`, {
    //   distance_km: transfer.distance_km,
    //   duration_min: transfer.duration_min,
    //   distance_km_type: typeof transfer.distance_km,
    //   duration_min_type: typeof transfer.duration_min
    // });
    
    return {
      distance: (transfer.distance_km !== null && transfer.distance_km !== undefined) 
                ? transfer.distance_km 
                : '-',
      duration: (transfer.duration_min !== null && transfer.duration_min !== undefined) 
                ? transfer.duration_min 
                : '-'
    };
  };


  return (
    <div className={`bg-white rounded-lg shadow-lg p-5 flex flex-col transform transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${
      isDraggingOver ? 'bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-400 border-dashed shadow-2xl' : ''
    }`}
    style={{
      position: 'relative',
      zIndex: isDraggingOver ? 10 : 1
    }}>
      {/* Şoför Bilgileri */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <svg className="w-6 h-6 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
          </svg>
          <div>
            <p className="font-semibold text-lg text-gray-900">{driver.user_name || 'İsimsiz Şoför'}</p>
            <p className="text-sm text-gray-600">{driver.user_phone || 'Telefon yok'}</p>
          </div>
        </div>
        <div className="text-right">
          {isWorking && displayShift ? (
            <div>
              <p className="font-medium text-gray-600">{getShiftTypeLabel(displayShift.shift_type)}</p>
              <p className="text-sm font-semibold text-gray-900">
                {formatTime(displayShift.start_time)} - {formatTime(displayShift.end_time)}
              </p>
            </div>
          ) : (
            <div>
              <p className="font-medium text-gray-600">Durum</p>
              <p className="text-sm font-semibold text-red-600">İzinli</p>
            </div>
          )}
        </div>
      </div>

      {/* Transfer Sayısı */}
      <div className="flex items-center justify-between bg-gray-100 p-3 rounded-lg mb-4">
        <span className="text-sm font-medium text-gray-600">Atanan Transferler</span>
        <span className="px-2.5 py-1 text-xs font-bold text-white bg-indigo-600 rounded-full">
          {driver.assignedTransfers ? driver.assignedTransfers.length : 0}
        </span>
      </div>

      {/* Transfer Listesi - Draggable */}
      <div className="flex-grow">
        {driver.assignedTransfers && driver.assignedTransfers.length > 0 ? (
          <div className="space-y-3">
            {driver.assignedTransfers.map((transfer, index) => (
              <Draggable 
                key={`transfer-${transfer.id}`} 
                draggableId={`transfer-${transfer.id}`} 
                index={index}
                type="TRANSFER"
              >
                {(provided, snapshot) => (
                  <div 
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    className={`bg-gray-50 rounded-xl p-4 shadow-sm cursor-move transition-all select-none ${
                      snapshot.isDragging ? 'shadow-2xl rotate-2 scale-110 bg-white border-2 border-blue-400' : 'hover:shadow-md'
                    }`}
                    style={{
                      ...provided.draggableProps.style,
                      userSelect: 'none',
                      WebkitUserSelect: 'none',
                      MozUserSelect: 'none',
                      msUserSelect: 'none',
                      zIndex: snapshot.isDragging ? 9999 : 'auto',
                      position: snapshot.isDragging ? 'fixed' : 'relative'
                    }}
                  >
                    {/* Transfer Saati ve Status */}
                    <div className="flex justify-between items-start mb-2 pointer-events-none">
                      <p className="text-xs font-bold text-blue-600">{transfer.pickup_time}</p>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${calculateProgress(transfer).badgeClass}`}>
                        {calculateProgress(transfer).label}
                      </span>
                    </div>

                    {/* Progress Bar */}
                    <div className="mb-3 pointer-events-none">
                      <div className="relative pt-1">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs font-medium text-gray-700">
                            {calculateProgress(transfer).label}
                          </span>
                          <span className="text-xs text-gray-500">
                            {calculateProgress(transfer).progress}%
                          </span>
                        </div>
                        <div className="overflow-hidden h-3 mb-2 text-xs flex rounded bg-gray-200">
                          <div 
                            className={`shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center ${calculateProgress(transfer).color} transition-all duration-500 ease-out`}
                            style={{ width: `${calculateProgress(transfer).progress}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>

                    {/* Lokasyon Bilgileri */}
                    <div className="flex justify-between items-center mb-3 pointer-events-none">
                      {/* Başlangıç */}
                      <div className="flex items-center gap-2 text-left w-2/5">
                        <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                        </svg>
                        <div>
                          <p className="text-sm font-medium text-gray-900 leading-tight">
                            {transfer.pickup_addr.split(',')[0] || transfer.pickup_addr}
                          </p>
                          {transfer.pickup_addr.split(',').slice(1).join(',').trim() && (
                            <p className="text-xs text-gray-600 leading-tight">
                              {transfer.pickup_addr.split(',').slice(1).join(',').trim()}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Mesafe/Süre */}
                      <div className="flex flex-col items-center text-xs text-gray-600">
                        <div className="flex items-center">
                          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                          </svg>
                          <span>{getDistanceData(transfer).distance}km</span>
                        </div>
                        <div className="w-full border-t border-dashed border-gray-400 my-1"></div>
                        <div className="flex items-center">
                          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                          </svg>
                          <span>{getDistanceData(transfer).duration} dk</span>
                        </div>
                      </div>

                      {/* Hedef */}
                      <div className="flex items-center gap-2 text-right w-2/5 justify-end">
                        <div>
                          <p className="text-sm font-medium text-gray-900 leading-tight">
                            {transfer.dropoff_addr.split(',')[0] || transfer.dropoff_addr}
                          </p>
                          {transfer.dropoff_addr.split(',').slice(1).join(',').trim() && (
                            <p className="text-xs text-gray-600 leading-tight">
                              {transfer.dropoff_addr.split(',').slice(1).join(',').trim()}
                            </p>
                          )}
                        </div>
                        <svg className="w-4 h-4 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>

                    {/* Yolcu Bilgileri */}
                    <div className="border-t border-gray-200 pt-2 pointer-events-none">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">
                            {transfer.passenger_name || 'İsimsiz'} ({transfer.pax_count || 1} Kişi)
                          </p>
                          {transfer.notes && (
                            <p className="text-xs text-gray-600 mt-0.5">
                              Not: {transfer.notes}
                            </p>
                          )}
                        </div>
                        <span className={`text-xs font-semibold px-2.5 py-0.5 rounded ${getAgencyBadgeColor(transfer.agency_name || 'BHT Clinic Admin')}`}>
                          {transfer.agency_name || 'BHT Clinic Admin'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </Draggable>
            ))}
          </div>
        ) : isWorking ? (
          <div className={`flex-grow flex items-center justify-center min-h-[120px] rounded-lg transition-all duration-300 ${
            isDraggingOver 
              ? 'bg-gradient-to-br from-blue-100 to-blue-200 border-2 border-blue-400 border-dashed shadow-inner' 
              : 'bg-gray-100 border border-gray-200 border-dashed'
          }`}>
            <div className="text-center">
              {isDraggingOver ? (
                <div className="animate-bounce">
                  <svg className="w-10 h-10 mx-auto mb-2 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 3L8.5 8.5H3l4.5 3.5L6 17l4-3 4 3-1.5-5.5L17 8.5h-5.5L10 3z"/>
                  </svg>
                  <p className="text-blue-700 font-semibold text-base">
                    📍 Buraya bırakın
                  </p>
                </div>
              ) : (
                <div>
                  <svg className="w-8 h-8 mx-auto mb-2 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 3L8.5 8.5H3l4.5 3.5L6 17l4-3 4 3-1.5-5.5L17 8.5h-5.5L10 3z"/>
                  </svg>
                  <p className="text-gray-600 text-base">
                    Henüz transfer atanmadı
                  </p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="flex-grow flex items-center justify-center">
            <p className="text-gray-600 text-base">İzinli</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Transfer Oluşturma Modal Component'i
const TransferCreateModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    pickup_addr: '',
    dropoff_addr: '',
    pickup_date: '',
    pickup_time: '',
    pax_count: 1,
    passenger_name: '',
    passenger_id_no: '',
    service_type: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Admin agency_id'sini 1 olarak sabit set ediyoruz (admin transferi)
      const submitData = {
        ...formData,
        agency_id: 1, // Admin agency ID
        service_type: formData.service_type || null
      };

      const response = await api.post('/api/transfers/', submitData);
      
      if (response.status === 200 || response.status === 201) {
        onSuccess && onSuccess();
      }
    } catch (error) {
      console.error('Transfer talebi oluşturulamadı:', error);
      setError(error.response?.data?.detail || 'Transfer talebi oluşturulamadı. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Bugünün tarihini default olarak set et (Local timezone)
  useEffect(() => {
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    setFormData(prev => ({ ...prev, pickup_date: today }));
  }, []);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={handleOverlayClick}>
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-bold text-gray-900">Yeni Transfer Talebi</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Alış Adresi */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Alış Adresi *
                </label>
                <AddressAutocomplete
                  value={formData.pickup_addr}
                  onChange={(value) => setFormData(prev => ({ ...prev, pickup_addr: value }))}
                  type="pickup"
                  placeholder="Alış adresi giriniz"
                  required
                />
              </div>

              {/* Bırakış Adresi */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bırakış Adresi *
                </label>
                <AddressAutocomplete
                  value={formData.dropoff_addr}
                  onChange={(value) => setFormData(prev => ({ ...prev, dropoff_addr: value }))}
                  type="dropoff"
                  placeholder="Bırakış adresi giriniz"
                  required
                />
              </div>

              {/* Alış Tarihi */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Alış Tarihi *
                </label>
                <input
                  type="date"
                  name="pickup_date"
                  value={formData.pickup_date}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500"
                  required
                />
              </div>

              {/* Alış Saati */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Alış Saati *
                </label>
                <input
                  type="time"
                  name="pickup_time"
                  value={formData.pickup_time}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500"
                  required
                />
              </div>

              {/* Yolcu Sayısı */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Yolcu Sayısı *
                </label>
                <input
                  type="number"
                  name="pax_count"
                  value={formData.pax_count}
                  onChange={handleChange}
                  min="1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500"
                  required
                />
              </div>

              {/* Hizmet Tipi */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Hizmet Tipi
                </label>
                <select
                  name="service_type"
                  value={formData.service_type}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500"
                >
                  <option value="">Seçiniz</option>
                  <option value="PATIENT">Hasta</option>
                  <option value="STAFF">Personel</option>
                  <option value="EXECUTIVE">Yönetici</option>
                  <option value="DOCUMENT">Evrak</option>
                  <option value="AIRPORT">Havalimanı Transfer</option>
                  <option value="MATERIAL">Malzeme</option>
                </select>
              </div>

              {/* Yolcu Adı */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Yolcu Adı
                </label>
                <input
                  type="text"
                  name="passenger_name"
                  value={formData.passenger_name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500"
                  placeholder="Yolcu adı giriniz"
                />
              </div>

              {/* Yolcu TC/ID */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  TC/Pasaport No
                </label>
                <input
                  type="text"
                  name="passenger_id_no"
                  value={formData.passenger_id_no}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500"
                  placeholder="TC kimlik veya pasaport no"
                />
              </div>

              {/* Notlar */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notlar
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500"
                  placeholder="Eklemek istediğiniz notlar..."
                />
              </div>
            </div>

            {/* Submit Button */}
            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
              >
                İptal
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-md hover:bg-red-600 disabled:opacity-50 flex items-center space-x-2"
              >
                {loading ? (
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Oluşturuluyor...</span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                    <span>Talep Oluştur</span>
                  </div>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Transfer Düzenleme Modal Component'i
const TransferEditModal = ({ transfer, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    pickup_addr: transfer.pickup_addr || '',
    dropoff_addr: transfer.dropoff_addr || '',
    pickup_date: transfer.pickup_date || '',
    pickup_time: transfer.pickup_time || '',
    pax_count: transfer.pax_count || 1,
    passenger_name: transfer.passenger_name || '',
    passenger_id_no: transfer.passenger_id_no || '',
    service_type: transfer.service_type || '',
    notes: transfer.notes || '',
    responsible_person: transfer.responsible_person || '',
    responsible_contact: transfer.responsible_contact || '',
    flight_code: transfer.flight_code || ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const submitData = {
        ...formData,
        service_type: formData.service_type || null
      };

      const response = await api.put(`/api/transfers/${transfer.id}`, submitData);
      
      if (response.status === 200) {
        onSuccess && onSuccess();
      }
    } catch (error) {
      console.error('Transfer güncellenemedi:', error);
      setError(error.response?.data?.detail || 'Transfer güncellenemedi. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={handleOverlayClick}>
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-bold text-gray-900">Transfer Düzenle #{transfer.id}</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Alış Adresi */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Alış Adresi *
                </label>
                <AddressAutocomplete
                  value={formData.pickup_addr}
                  onChange={(value) => setFormData(prev => ({ ...prev, pickup_addr: value }))}
                  type="pickup"
                  placeholder="Alış adresi giriniz"
                  required
                />
              </div>

              {/* Bırakış Adresi */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bırakış Adresi *
                </label>
                <AddressAutocomplete
                  value={formData.dropoff_addr}
                  onChange={(value) => setFormData(prev => ({ ...prev, dropoff_addr: value }))}
                  type="dropoff"
                  placeholder="Bırakış adresi giriniz"
                  required
                />
              </div>

              {/* Alış Tarihi */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Alış Tarihi *
                </label>
                <input
                  type="date"
                  name="pickup_date"
                  value={formData.pickup_date}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              {/* Alış Saati */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Alış Saati *
                </label>
                <input
                  type="time"
                  name="pickup_time"
                  value={formData.pickup_time}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              {/* Yolcu Sayısı */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Yolcu Sayısı *
                </label>
                <input
                  type="number"
                  name="pax_count"
                  value={formData.pax_count}
                  onChange={handleChange}
                  min="1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              {/* Hizmet Tipi */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Hizmet Tipi
                </label>
                <select
                  name="service_type"
                  value={formData.service_type}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Seçiniz</option>
                  <option value="PATIENT">Hasta</option>
                  <option value="STAFF">Personel</option>
                  <option value="EXECUTIVE">Yönetici</option>
                  <option value="DOCUMENT">Evrak</option>
                  <option value="AIRPORT">Havalimanı Transfer</option>
                  <option value="MATERIAL">Malzeme</option>
                </select>
              </div>

              {/* Yolcu Adı */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Yolcu Adı
                </label>
                <input
                  type="text"
                  name="passenger_name"
                  value={formData.passenger_name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Yolcu adı giriniz"
                />
              </div>

              {/* Yolcu TC/Pasaport/Evrak No */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  TC/Pasaport/Evrak No
                </label>
                <input
                  type="text"
                  name="passenger_id_no"
                  value={formData.passenger_id_no}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="TC No, Pasaport No veya Evrak No"
                />
              </div>

              {/* Sorumlu Kişi */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sorumlu Kişi
                </label>
                <input
                  type="text"
                  name="responsible_person"
                  value={formData.responsible_person}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Sorumlu kişi adı"
                />
              </div>

              {/* Sorumlu İletişim */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sorumlu İletişim
                </label>
                <input
                  type="text"
                  name="responsible_contact"
                  value={formData.responsible_contact}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Telefon numarası"
                />
              </div>

              {/* Uçuş Kodu */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Uçuş Kodu
                </label>
                <input
                  type="text"
                  name="flight_code"
                  value={formData.flight_code}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Örn: TK123"
                />
              </div>

              {/* Notlar */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notlar
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Eklemek istediğiniz notlar..."
                />
              </div>
            </div>

            {/* Submit Button */}
            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
              >
                İptal
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-500 rounded-md hover:bg-blue-600 disabled:opacity-50 flex items-center space-x-2"
              >
                {loading ? (
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Güncelleniyor...</span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Güncelle</span>
                  </div>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TransferPlani1;